import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { getAuthToken } from "../utils/cookieUtils";
import axios from "axios";
import { useDateAndAdmin } from "../utils/dateUtils";
import Pagination from "../components/pagination";
import { Parser } from "json2csv";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

function ActionLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
    from: 0,
    to: 0,
    next_page_url: null,
    prev_page_url: null
  });
  const { formattedDate, adminData } = useDateAndAdmin();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationDropdownOpen && !event.target.closest('.location-filter')) {
        setLocationDropdownOpen(false);
      }
      if (datePickerOpen && !event.target.closest('.date-filter')) {
        setDatePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [locationDropdownOpen, datePickerOpen]);

  // Fetch locations for dropdown filter
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get("/super-admin/locations", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            per_page: 1000 // Get all locations
          }
        });
        const locationsData = response.data?.data?.data || [];
        setLocations(Array.isArray(locationsData) ? locationsData : []);
      } catch (err) {
        console.error("Error fetching locations:", err);
      }
    };

    fetchLocations();
  }, []);

  // Fetch action logs without search
  useEffect(() => {
    const fetchActionLogs = async (page = 1, locationId = "", date = null) => {
      try {
        setLoading(true);
        const token = getAuthToken();
        const response = await axios.get("/super-admin/action-logs", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page,
            ...(locationId && { location_id: locationId }),
            ...(date && { date: date.toLocaleDateString('en-CA') })
          }
        });
        const logsData = response.data.message.data || [];
        setLogs(Array.isArray(logsData) ? logsData : []);
        setPagination({
          current_page: response.data.message.current_page,
          last_page: response.data.message.last_page,
          per_page: response.data.message.per_page,
          total: response.data.message.total,
          from: response.data.message.from,
          to: response.data.message.to,
          next_page_url: response.data.message.next_page_url,
          prev_page_url: response.data.message.prev_page_url
        });
        setError(null);
      } catch (err) {
        setError("Failed to fetch action logs");
        console.error("Error fetching action logs:", err);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if there's no search term
    if (!searchTerm) {
      fetchActionLogs(pagination.current_page, selectedLocationId, selectedDate);
    }
  }, [pagination.current_page, selectedLocationId, selectedDate, searchTerm]);

  // Search action logs with form fields
  useEffect(() => {
    const searchActionLogs = async (page = 1, search = "") => {
      try {
        setLoading(true);
        const token = getAuthToken();
        const formData = new FormData();
        
        // Add search term (try both name and email only)
        formData.append('name', search);
        formData.append('email', search);
        formData.append('page', page);

        const response = await axios.post("/super-admin/action-logs/search", formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        const logsData = response.data.message.data || [];
        setLogs(Array.isArray(logsData) ? logsData : []);
        setPagination({
          current_page: response.data.message.current_page,
          last_page: response.data.message.last_page,
          per_page: response.data.message.per_page,
          total: response.data.message.total,
          from: response.data.message.from,
          to: response.data.message.to,
          next_page_url: response.data.message.next_page_url,
          prev_page_url: response.data.message.prev_page_url
        });
        setError(null);
      } catch (err) {
        setError("Failed to search action logs");
        console.error("Error searching action logs:", err);
      } finally {
        setLoading(false);
      }
    };

    // Only search if there's a search term
    if (searchTerm) {
      searchActionLogs(pagination.current_page, searchTerm);
    }
  }, [pagination.current_page, searchTerm]);

  const handleLocationFilter = (locationId) => {
    setSelectedLocationId(locationId);
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setLocationDropdownOpen(false);
  };

  const handleDateFilter = (date) => {
    setSelectedDate(date);
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setDatePickerOpen(false);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, current_page: newPage }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleExportActionLogsCSV = () => {
    if (logs.length === 0) {
      toast.error("No data available to export");
      return;
    }
    const fields = [
      "id",
      "user_id", 
      "action",
      "location",
      "details",
      "ip_address",
      "created_at"
    ];
    const opts = { fields };
    const parser = new Parser(opts);
    const csv = parser.parse(logs);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    
    const filename = searchTerm || selectedLocationId || selectedDate
      ? `action_logs_filtered_page_${pagination.current_page}.csv`
      : `action_logs_page_${pagination.current_page}.csv`;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    const successMessage = `Current page (${logs.length} records) exported successfully!`;
    toast.success(successMessage);
  };

  // Export all logs from API to CSV (respects current search)
  const handleExportAllActionLogsCSV = async () => {
    try {
      const token = getAuthToken();
      let response;

      if (searchTerm) {
        // Use search endpoint if there's a search term (name and email only)
        const formData = new FormData();
        formData.append('name', searchTerm);
        formData.append('email', searchTerm);
        formData.append('per_page', 10000);

        response = await axios.post("/super-admin/action-logs/search", formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Use regular endpoint if no search term
        response = await axios.get("/super-admin/action-logs", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            per_page: 10000,
            ...(selectedLocationId && { location_id: selectedLocationId }),
            ...(selectedDate && { date: selectedDate.toLocaleDateString('en-CA') })
          }
        });
      }
      
      const allLogsData = response.data.message?.data || [];
      if (allLogsData.length === 0) {
        toast.error("No data available to export");
        return;
      }

      const fields = [
        "id",
        "user_id",
        "action", 
        "location",
        "details",
        "ip_address",
        "created_at"
      ];
      const opts = { fields };
      const parser = new Parser(opts);
      const csv = parser.parse(allLogsData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename = searchTerm 
        ? `action_logs_search_${searchTerm.replace(/[^a-z0-9]/gi, '_')}.csv`
        : "all_action_logs.csv";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      const successMessage = searchTerm 
        ? `Action logs matching "${searchTerm}" exported successfully!`
        : "All action logs exported successfully!";
      toast.success(successMessage);
    } catch (error) {
      console.error("Error fetching logs or exporting CSV:", error);
      toast.error("An error occurred while exporting the data.");
    }
  };

  return (
          <div className="dashboard">
            <div className="Nav">
              <Sidebar />
            </div>
    
            <div className="dashboard-display">
              <div className="top-bar container-fluid">
                <div className="top-bar-info">
                  <p id="date">{formattedDate}</p>
                  <b>
                    <p>Welcome back {adminData}</p>
                  </b>
                </div>
              </div>
              <div className="main-dashboard container-fluid sub-dashboard">
                <div
                  style={{ padding: "24px", paddingBottom: "0", paddingTop: "0" }}
                  className="main-settings staff-items"
                >
                  <div className="search">
                    <input
                      type="text"
                      placeholder="Search by email or name..."
                      value={searchTerm}
                      onChange={handleSearch}
                    />
                  </div>
                  <div className="export-group">
                    <div className="location-filter">
                      <button
                        className={`location-filter-button ${selectedLocationId ? 'active' : ''}`}
                        onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
                      >
                        <span>
                          {selectedLocationId 
                            ? locations.find(loc => loc.id.toString() === selectedLocationId)?.name || 'Select Location'
                            : 'All Locations'
                          }
                        </span>
                        <span className={`location-filter-arrow ${locationDropdownOpen ? 'open' : ''}`}>
                          <svg className="filter-arrow-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      </button>
                      {locationDropdownOpen && (
                        <div className="location-filter-dropdown">
                          <div
                            className={`location-filter-option ${!selectedLocationId ? 'selected' : ''}`}
                            onClick={() => handleLocationFilter("")}
                          >
                            All Locations
                          </div>
                          {locations.map(location => (
                            <div
                              key={location.id}
                              className={`location-filter-option ${selectedLocationId === location.id.toString() ? 'selected' : ''}`}
                              onClick={() => handleLocationFilter(location.id.toString())}
                            >
                              {location.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="date-filter">
                      <button
                        className={`date-filter-button ${selectedDate ? 'active' : ''}`}
                        onClick={() => setDatePickerOpen(!datePickerOpen)}
                      >
                        <span>
                          {selectedDate 
                            ? selectedDate.toLocaleDateString()
                            : 'Select Date'
                          }
                        </span>
                        <span className={`date-filter-arrow ${datePickerOpen ? 'open' : ''}`}>
                          <svg className="filter-arrow-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      </button>
                      {datePickerOpen && (
                        <div className="date-filter-dropdown">
                          <DatePicker
                            selected={selectedDate}
                            onChange={handleDateFilter}
                            inline
                            dateFormat="yyyy-MM-dd"
                          />
                          <div className="date-filter-clear">
                            <button 
                              className="date-filter-clear-button"
                              onClick={() => handleDateFilter(null)}
                            >
                              Clear Date
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="export">
                      <button onClick={handleExportAllActionLogsCSV}>
                        <img
                          src={`${process.env.PUBLIC_URL}/images/export-icon.png`}
                          alt="Export"
                        />{" "}
                        Export all
                      </button>
                    </div>
                    <div className="export">
                      <button onClick={handleExportActionLogsCSV}>
                        <img
                          src={`${process.env.PUBLIC_URL}/images/export-icon.png`}
                          alt="Export"
                        />{" "}
                        Export Page
                      </button>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: "0" }} className="show-admin-table">
                  <table className="admin-table member-table">
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>User</th>
                        <th>Action</th>
                        <th>Location</th>
                        <th>Date</th>
                        <th>IP Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7">
                            <div className="spinner"></div>
                          </td>
                        </tr>
                      ) : error ? (
                        <tr>
                          <td colSpan="7" style={{ textAlign: "center", color: "red" }}>{error}</td>
                        </tr>
                      ) : logs.length === 0 ? (
                        <tr>
                          <td colSpan="7" style={{ textAlign: "center" }}>No action logs found.</td>
                        </tr>
                      ) : (
                        logs.map((log, index) => (
                          <tr key={log.id}>
                            <td>{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                            <td>{log.details?.split(' ')[0] || '-'}</td>
                            <td style={{ textTransform: 'capitalize' }}>{log.action}</td>
                            <td>{log.location || '-'}</td>
                            <td>{formatDate(log.created_at)}</td>
                            <td>{log.ip_address || '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  <Pagination
                    currentPageProp={pagination.current_page}
                    totalPageProp={pagination.last_page}
                    onPageChange={handlePageChange}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          </div>
  );
}

export default ActionLog;
